import { useState, useRef } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { X, FolderOpen, Loader2, FileJson, AlertCircle, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import { Label } from '@/components/ui/label';

const APP_JSON = 'app.json';

/** Same as mz import: all files from folder, path with "/". */
interface ImportFileRef {
    path: string;
    content: string;
}

type ImportStep = 'select' | 'importing' | 'result';

interface ExpectedCapabilityRow {
    path: string;
    name: string;
    type: string;
}

interface ApplicationImportResultRow {
    name: string;
    type: string;
    action: string;
}

interface ApplicationImportErrorRow {
    name?: string;
    path?: string;
    code: string;
    message: string;
}

interface ApplicationImportResponse {
    app_key?: string;
    imported?: ApplicationImportResultRow[];
    errors?: ApplicationImportErrorRow[];
    dry_run?: boolean;
}

function parseExpectedCapabilityManifests(files: ImportFileRef[]): ExpectedCapabilityRow[] {
    const seen = new Set<string>();
    const out: ExpectedCapabilityRow[] = [];
    for (const f of files) {
        if (!f.path.toLowerCase().endsWith('.json')) continue;
        try {
            const raw = JSON.parse(f.content) as Record<string, unknown>;
            const type = raw.type;
            const name = raw.name;
            if (typeof type !== 'string' || typeof name !== 'string') continue;
            if (type === 'application') continue;
            const key = `${type}:${name}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ path: f.path, name, type });
        } catch {
            // not JSON
        }
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
}

interface AppImportDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onImportSuccess?: () => void;
}

export default function AppImportDrawer({
    isOpen,
    onClose,
    onImportSuccess,
}: AppImportDrawerProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [appManifest, setAppManifest] = useState<Record<string, unknown> | null>(null);
    /** All files from folder (same as mz import walkDir) for sending to registry import */
    const [allFiles, setAllFiles] = useState<ImportFileRef[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [importStep, setImportStep] = useState<ImportStep>('select');
    const [expectedCapabilities, setExpectedCapabilities] = useState<ExpectedCapabilityRow[]>([]);
    const [importSummary, setImportSummary] = useState<ApplicationImportResponse | null>(null);
    const [importNetworkError, setImportNetworkError] = useState<string | null>(null);

    const resetState = () => {
        setAppManifest(null);
        setAllFiles([]);
        setError(null);
        setValidationError(null);
        setImportStep('select');
        setExpectedCapabilities([]);
        setImportSummary(null);
        setImportNetworkError(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const readFile = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string) ?? '');
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file, 'utf-8');
        });
    };

    const handleFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;

        setError(null);
        setValidationError(null);
        setAppManifest(null);
        setAllFiles([]);

        try {
            const fileList = Array.from(files);
            let appJsonFile: File | null = null;
            const collected: ImportFileRef[] = [];

            // webkitRelativePath includes the selected folder as the first segment
            // (e.g. "definitions/app.json"). Strip it so paths match CLI walkDir output
            // (e.g. "app.json", "agents/foo/agent.json").
            const rootFolder = ((fileList[0] as File & { webkitRelativePath?: string }).webkitRelativePath ?? '')
                .replace(/\\/g, '/')
                .split('/')[0];
            const stripPrefix = rootFolder ? rootFolder + '/' : '';

            for (const file of fileList) {
                const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? file.name;
                const pathWithSlash = relativePath.replace(/\\/g, '/');
                const segments = pathWithSlash.split('/');
                if (segments.includes('node_modules') || segments.includes('.git')) continue;

                const stripped = stripPrefix && pathWithSlash.startsWith(stripPrefix)
                    ? pathWithSlash.slice(stripPrefix.length)
                    : pathWithSlash;

                const baseName = stripped.split('/').pop()?.toLowerCase() ?? '';
                if (baseName === APP_JSON) {
                    appJsonFile = file;
                }

                try {
                    const content = await readFile(file);
                    collected.push({ path: stripped, content });
                } catch {
                    // Skip binary or unreadable files (same as CLI: read as utf-8)
                }
            }

            if (!appJsonFile) {
                setValidationError(`No ${APP_JSON} found in the selected folder. Please select the root folder that contains ${APP_JSON}.`);
                return;
            }

            const appContent = collected.find((f) => f.path.toLowerCase().endsWith(APP_JSON))?.content;
            if (!appContent) {
                setValidationError(`No ${APP_JSON} found in the selected folder.`);
                return;
            }
            let appParsed: Record<string, unknown>;
            try {
                appParsed = JSON.parse(appContent) as Record<string, unknown>;
            } catch {
                setValidationError(`${APP_JSON} is not valid JSON.`);
                return;
            }

            const appKey = (appParsed.app_key as string) ?? (appParsed.name as string);
            if (!appKey || typeof appKey !== 'string') {
                setValidationError(`${APP_JSON} must contain "app_key" or "name".`);
                return;
            }

            setAppManifest(appParsed);
            setAllFiles(collected);
        } catch (err) {
            logger.error('AppImportDrawer:handleFolderChange - Failed to read folder', { err });
            setError('Failed to read folder or files.');
        }
    };

    const handleImport = async () => {
        if (allFiles.length === 0) return;

        setLoading(true);
        setError(null);
        setImportNetworkError(null);
        setImportSummary(null);
        setExpectedCapabilities(parseExpectedCapabilityManifests(allFiles));
        setImportStep('importing');

        const payload = { files: allFiles };

        try {
            const { data } = await api.post<ApplicationImportResponse>('/applications/import', payload);
            setImportSummary(data ?? {});
            setImportStep('result');
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
                ?? (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? (err as Error)?.message
                ?? 'Import failed';
            logger.error('AppImportDrawer:handleImport - Import failed', { err });
            setImportNetworkError(String(msg));
            setImportStep('result');
        } finally {
            setLoading(false);
        }
    };

    const handleReviewFiles = () => {
        setImportStep('select');
        setImportSummary(null);
        setImportNetworkError(null);
        setExpectedCapabilities([]);
    };

    const handleResultDone = () => {
        if (importNetworkError == null && importSummary != null) {
            const errs = importSummary.errors ?? [];
            if (errs.length > 0) {
                logger.warn('AppImportDrawer:handleResultDone - Import completed with errors', {
                    count: errs.length,
                    appKey: importSummary.app_key,
                });
            }
            onImportSuccess?.();
        }
        resetState();
        onClose();
    };

    const handleHeaderClose = () => {
        if (importStep === 'importing') return;
        if (importStep === 'result') {
            if (importNetworkError == null && importSummary != null) {
                handleResultDone();
            } else {
                resetState();
                onClose();
            }
            return;
        }
        resetState();
        onClose();
    };

    const primaryButtonClass =
        'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass =
        'inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50';

    const handleDrawerClose = () => {
        if (importStep === 'result') return;
        handleHeaderClose();
    };

    return (
        <SlideDrawer isOpen={isOpen} onClose={handleDrawerClose}>
                        <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 relative">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
                                        <FolderOpen className="h-5 w-5 text-violet-600" />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-slate-900">
                                        {importStep === 'importing'
                                            ? 'Import in progress'
                                            : importStep === 'result'
                                              ? 'Import results'
                                              : 'Import Application'}
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    disabled={importStep === 'importing'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleHeaderClose();
                                    }}
                                    className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900 disabled:opacity-40 disabled:pointer-events-none"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <p className="text-sm text-slate-500">
                                {importStep === 'select' && (
                                    <>
                                        Select the root folder that contains app.json (same as mz import). All files in the folder will be sent to the registry import.
                                    </>
                                )}
                                {importStep === 'importing' && (
                                    <>
                                        Sending files to the registry and importing capabilities. This can take a moment for large apps.
                                    </>
                                )}
                                {importStep === 'result' && (
                                    <>
                                        Review which capabilities were imported. The registry can return HTTP success with per-capability errors—check the list below.
                                    </>
                                )}
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 bg-white">
                            <div className="space-y-6 max-w-2xl mx-auto">
                                {importStep === 'select' && (
                                    <>
                                        <div className="space-y-2">
                                            <Label>Application folder</Label>
                                            <input
                                                ref={inputRef}
                                                type="file"
                                                // @ts-expect-error webkitdirectory is supported in modern browsers
                                                webkitdirectory=""
                                                directory=""
                                                className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100"
                                                onChange={handleFolderChange}
                                            />
                                        </div>

                                        {(validationError ?? error) && (
                                            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-700">
                                                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                                                <span>{validationError ?? error}</span>
                                            </div>
                                        )}

                                        {appManifest && (
                                            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                                                <div className="flex items-center gap-2 text-slate-700">
                                                    <FileJson size={18} />
                                                    <span className="font-medium">
                                                        {(appManifest.display_name as string) ?? (appManifest.name as string) ?? 'Application'}
                                                    </span>
                                                    <span className="text-slate-500 font-mono text-sm">
                                                        ({(appManifest.app_key as string) ?? (appManifest.name as string)})
                                                    </span>
                                                </div>
                                                {allFiles.length > 0 && (
                                                    <p className="text-sm text-slate-600">
                                                        {allFiles.length} file(s) will be imported (same as mz import).
                                                    </p>
                                                )}
                                                {allFiles.length > 0 && (
                                                    <ul className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white p-2 text-sm font-mono text-slate-600">
                                                        {allFiles.map((f) => (
                                                            <li key={f.path} className="truncate py-0.5">
                                                                {f.path}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}

                                {importStep === 'importing' && (
                                    <div className="space-y-6">
                                        <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-4">
                                            <div className="mb-3 flex items-center gap-2 font-medium text-indigo-950">
                                                <Loader2 size={18} className="animate-spin text-indigo-600" />
                                                Working…
                                            </div>
                                            <p className="text-sm text-indigo-900/80">
                                                Uploading {allFiles.length} file(s). Capabilities are processed on the server in one step; the list below shows what we detected in your folder.
                                            </p>
                                            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-indigo-200/50">
                                                <div className="h-full w-full animate-pulse rounded-full bg-indigo-500/70" />
                                            </div>
                                        </div>
                                        <div>
                                            <p className="mb-2 text-sm font-medium text-slate-700">Capabilities detected</p>
                                            <ul className="max-h-[min(24rem,45vh)] overflow-y-auto rounded-lg border border-slate-200 bg-white">
                                                {expectedCapabilities.length === 0 ? (
                                                    <li className="p-4 text-sm text-slate-500">
                                                        No capability JSON manifests found (only the application or non-manifest files). Files are still being sent—results will show any registry errors.
                                                    </li>
                                                ) : (
                                                    expectedCapabilities.map((c) => (
                                                        <li
                                                            key={`${c.type}:${c.name}`}
                                                            className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5 text-sm last:border-0"
                                                        >
                                                            <Loader2 size={16} className="shrink-0 animate-spin text-indigo-500" />
                                                            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                                                                {c.type}
                                                            </span>
                                                            <span className="min-w-0 truncate font-medium text-slate-800">{c.name}</span>
                                                        </li>
                                                    ))
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                {importStep === 'result' && (
                                    <div className="space-y-5">
                                        {importNetworkError && (
                                            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-800">
                                                <XCircle size={18} className="shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="font-medium">Import request failed</p>
                                                    <p className="mt-1">{importNetworkError}</p>
                                                </div>
                                            </div>
                                        )}

                                        {!importNetworkError && importSummary && (
                                            <>
                                                {(importSummary.errors?.length ?? 0) > 0 && (
                                                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/90 p-3 text-sm text-amber-950">
                                                        <AlertCircle size={18} className="shrink-0 mt-0.5 text-amber-700" />
                                                        <div>
                                                            <p className="font-medium">Completed with errors</p>
                                                            <p className="mt-1 text-amber-900/90">
                                                                {importSummary.errors?.length} issue(s) reported. The application may be partially imported—fix manifests and try again.
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                                {(importSummary.errors?.length ?? 0) === 0 && (
                                                    <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-sm text-emerald-900">
                                                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                                                        <span>Import finished with no registry errors.</span>
                                                    </div>
                                                )}
                                                {importSummary.app_key && (
                                                    <p className="text-sm text-slate-600">
                                                        Application key:{' '}
                                                        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-800">
                                                            {importSummary.app_key}
                                                        </code>
                                                    </p>
                                                )}

                                                <div>
                                                    <p className="mb-2 text-sm font-medium text-slate-700">Capabilities</p>
                                                    <ul className="max-h-[min(14rem,30vh)] overflow-y-auto rounded-lg border border-slate-200 bg-white">
                                                        {expectedCapabilities.length === 0 &&
                                                            (importSummary.imported ?? []).filter((r) => r.type !== 'application').length === 0 && (
                                                                <li className="p-3 text-sm text-slate-500">No capability rows to show.</li>
                                                            )}
                                                        {expectedCapabilities.map((c) => {
                                                            const errs = (importSummary.errors ?? []).filter((e) => e.name === c.name);
                                                            const hit = (importSummary.imported ?? []).find((i) => i.name === c.name);
                                                            const status: 'ok' | 'err' | 'miss' =
                                                                errs.length > 0 ? 'err' : hit ? 'ok' : 'miss';
                                                            return (
                                                                <li
                                                                    key={`${c.type}:${c.name}`}
                                                                    className="flex items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-sm last:border-0"
                                                                >
                                                                    {status === 'ok' && (
                                                                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                                                                    )}
                                                                    {status === 'err' && (
                                                                        <XCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
                                                                    )}
                                                                    {status === 'miss' && (
                                                                        <MinusCircle size={16} className="mt-0.5 shrink-0 text-slate-400" />
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                                                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                                                                                {c.type}
                                                                            </span>
                                                                            <span className="font-medium text-slate-800">{c.name}</span>
                                                                            {hit && (
                                                                                <span className="text-xs text-slate-500">({hit.action})</span>
                                                                            )}
                                                                        </div>
                                                                        {status === 'miss' && (
                                                                            <p className="mt-0.5 text-xs text-slate-500">
                                                                                Not reported as imported—may have been skipped or blocked by an earlier error.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>

                                                {(importSummary.imported ?? []).some((r) => r.type === 'application') && (
                                                    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-sm text-slate-700">
                                                        <span className="font-medium">Application manifest</span>
                                                        <span className="ml-2 text-slate-500">
                                                            {(importSummary.imported ?? []).find((r) => r.type === 'application')?.action ?? 'updated'}
                                                        </span>
                                                    </div>
                                                )}

                                                {(importSummary.errors?.length ?? 0) > 0 && (
                                                    <div>
                                                        <p className="mb-2 text-sm font-medium text-slate-700">Error details</p>
                                                        <ul className="max-h-[min(14rem,28vh)] space-y-2 overflow-y-auto rounded-lg border border-red-100 bg-red-50/40 p-3 text-sm text-red-900">
                                                            {(importSummary.errors ?? []).map((e, idx) => (
                                                                <li key={`${e.code}-${idx}-${e.message.slice(0, 24)}`} className="border-b border-red-100/80 pb-2 last:border-0 last:pb-0">
                                                                    <span className="font-mono text-xs text-red-700">[{e.code}]</span>{' '}
                                                                    {e.name && (
                                                                        <span className="font-medium">
                                                                            {e.name}
                                                                            {e.path ? ` · ${e.path}` : ''}:{' '}
                                                                        </span>
                                                                    )}
                                                                    {!e.name && e.path && (
                                                                        <span className="font-medium">{e.path}: </span>
                                                                    )}
                                                                    {e.message}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            {importStep === 'select' && (
                                <>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleHeaderClose();
                                        }}
                                        disabled={loading}
                                        className={secondaryButtonClass}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleImport();
                                        }}
                                        disabled={loading || allFiles.length === 0}
                                        className={primaryButtonClass}
                                    >
                                        Import
                                    </button>
                                </>
                            )}
                            {importStep === 'importing' && (
                                <span className="text-sm text-slate-500">Import in progress…</span>
                            )}
                            {importStep === 'result' && (
                                <>
                                    {(importNetworkError != null || (importSummary?.errors?.length ?? 0) > 0) && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleReviewFiles();
                                            }}
                                            className={secondaryButtonClass}
                                        >
                                            Back to files
                                        </button>
                                    )}
                                    {importNetworkError != null ? (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                resetState();
                                                onClose();
                                            }}
                                            className={primaryButtonClass}
                                        >
                                            Close
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleResultDone();
                                            }}
                                            className={primaryButtonClass}
                                        >
                                            Done
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
        </SlideDrawer>
    );
}
